/**
 * Time formatting for the panel.
 *
 * Every clock in a broadcast interface reads in the station's timezone, never the
 * viewer's. An operator checking the schedule from another country needs the time the
 * show goes out, and a panel that quietly switches to their local clock is worse than one
 * that shows no time at all: it looks right and is wrong.
 */

/** The station's timezone, set once at start-up. Amsterdam until the API says otherwise. */
let stationTimeZone = 'Europe/Amsterdam';

/**
 * The locale used for dates and weekday names in shared components.
 *
 * Held here rather than injected, so this package stays free of a framework's i18n
 * library: it is a component library and should be usable from anything. The panel sets
 * it when the operator changes language.
 */
let uiLocale = 'nl-NL';

export const DAY_MS = 86_400_000;

export function setUiLocale(locale: string): void {
  uiLocale = locale;
}

export function getUiLocale(): string {
  return uiLocale;
}

export function setStationTimeZone(timeZone: string): void {
  stationTimeZone = timeZone;
}

export function getStationTimeZone(): string {
  return stationTimeZone;
}

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** HH:MM in the station's timezone. */
export function formatClock(value: string | Date | null | undefined, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '--:--';

  return new Intl.DateTimeFormat(uiLocale, {
    timeZone: timeZone ?? stationTimeZone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/**
 * Date and time in the station's timezone and the UI locale, for timestamps that may be
 * days or months old: an audit entry, a recording, an alert. HH:MM alone is ambiguous
 * there.
 */
export function formatDateTime(value: string | Date | null | undefined, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';

  return new Intl.DateTimeFormat(uiLocale, {
    timeZone: timeZone ?? stationTimeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(date);
}

/** The calendar date in the station's timezone and the UI locale. */
export function formatDate(value: string | Date | null | undefined, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';

  return new Intl.DateTimeFormat(uiLocale, {
    timeZone: timeZone ?? stationTimeZone,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/**
 * The wall-clock offset of a zone at an instant, in milliseconds.
 *
 * Derived by formatting the instant in the zone and reading the result back as if it were
 * UTC. This is the only way to get a zone offset in the browser without a date library,
 * and it is correct across DST because the formatter applies the rules for that instant.
 */
export function zoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);

  const read = (type: string): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  const asUtc = Date.UTC(
    read('year'),
    read('month') - 1,
    read('day'),
    read('hour'),
    read('minute'),
    read('second'),
  );

  return asUtc - instant.getTime();
}

/**
 * The instant of 00:00 station-local on a day.
 *
 * Takes either an instant (the day containing it) or a `YYYY-MM-DD` day string as a date
 * input produces. The archive and the scheduler both need this: a day window built from
 * the viewer's local midnight is an hour or more wrong for anyone abroad, and a day that
 * crosses a clock change is not 24 hours long.
 */
export function zonedDayStart(day: string | Date, timeZone: string = stationTimeZone): Date {
  let localMidnight: number;
  let offsetGuess: number;

  if (typeof day === 'string') {
    const [year = 0, month = 1, date = 1] = day.split('-').map(Number);
    localMidnight = Date.UTC(year, month - 1, date);
    offsetGuess = zoneOffsetMs(new Date(localMidnight), timeZone);
  } else {
    offsetGuess = zoneOffsetMs(day, timeZone);
    localMidnight = Math.floor((day.getTime() + offsetGuess) / DAY_MS) * DAY_MS;
  }

  // The offset at midnight can differ from the offset at the reference instant on a DST
  // day, so the first answer is a guess that is then corrected using the offset where it
  // landed.
  const guess = localMidnight - offsetGuess;
  const corrected = localMidnight - zoneOffsetMs(new Date(guess), timeZone);
  return new Date(corrected);
}

/** The `YYYY-MM-DD` day string of an instant in the station's timezone. */
export function toDayString(instant: Date, timeZone: string = stationTimeZone): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const read = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${read('year')}-${read('month')}-${read('day')}`;
}
